import { useCallback } from 'react';
import type {
  EntitySearchDateRange,
  EntitySearchEntityType,
} from 'types/api-payloads';
import type { SetEntitySearchPayload } from './types';

/**
 * The search's filter and table handlers, each starting again from page 1
 * except paging itself.
 * @param {SetEntitySearchPayload} setPayload Updates the search.
 * @param {EntitySearchEntityType[] | null} lockedEntityTypes The types it is locked to.
 * @return {object} The handlers.
 */
export function useEntitySearchHandlers(
  setPayload: SetEntitySearchPayload,
  lockedEntityTypes: EntitySearchEntityType[] | null
) {
  const typeFilterLocked = lockedEntityTypes !== null;

  const onCreatedAtChange = useCallback(
    (createdAt?: EntitySearchDateRange) =>
      setPayload((current) => ({ ...current, page: 1, createdAt })),
    [setPayload]
  );

  const onUpdatedAtChange = useCallback(
    (updatedAt?: EntitySearchDateRange) =>
      setPayload((current) => ({ ...current, page: 1, updatedAt })),
    [setPayload]
  );

  const onEntityTypesChange = useCallback(
    (types: EntitySearchEntityType[]) => {
      if (typeFilterLocked) return;
      setPayload((current) => ({
        ...current,
        page: 1,
        entityTypes: types.length > 0 ? types : undefined,
      }));
    },
    [setPayload, typeFilterLocked]
  );

  const onEntityTypeFilterChange = useCallback(
    (includePrimary: boolean, includeAliases: boolean) =>
      setPayload((current) => ({
        ...current,
        page: 1,
        includePrimary,
        includeAliases,
      })),
    [setPayload]
  );

  const onReset = useCallback(
    () =>
      setPayload((current) => ({
        ...current,
        page: 1,
        createdAt: undefined,
        updatedAt: undefined,
        entityTypes: lockedEntityTypes ?? undefined,
        includePrimary: true,
        includeAliases: true,
      })),
    [setPayload, lockedEntityTypes]
  );

  const onSearchChange = useCallback(
    (text: string) =>
      setPayload((current) => ({
        ...current,
        page: 1,
        text: text || undefined,
        sortAttribute: undefined,
        sortDirection: undefined,
      })),
    [setPayload]
  );

  const onSortChange = useCallback(
    (
      sortAttribute: string | undefined,
      sortDirection: 'ASC' | 'DESC' | undefined
    ) =>
      setPayload((current) => ({
        ...current,
        page: 1,
        sortAttribute,
        sortDirection,
      })),
    [setPayload]
  );

  const onPaginationChange = useCallback(
    (page: number, pageSize: number) =>
      setPayload((current) => ({ ...current, page, pageSize })),
    [setPayload]
  );

  return {
    onCreatedAtChange,
    onUpdatedAtChange,
    onEntityTypesChange,
    onEntityTypeFilterChange,
    onReset,
    onSearchChange,
    onSortChange,
    onPaginationChange,
  };
}
